import LinkButton from "../Buttons/LinkButton";

/**
 * ImpactStatsSection
 *
 * A stats showcase section with two pill-shaped images and four stat cards
 * arranged in a 4-column grid on desktop. On mobile, the images stack above
 * and below a 2x2 grid of cards.
 *
 * Used on consulting service pages to highlight measurable impact numbers.
 *
 * Props:
 * - bgColor (string, optional): Tailwind bg class for the section. Defaults to "bg-blue/20".
 * - title (string | ReactNode, required): Section heading. An orange period is appended automatically.
 * - description (string | ReactNode, required): Paragraph below the heading.
 * - image1Src (string, required): URL of the top/left pill image.
 * - image1Alt (string, required): Alt text for the first image.
 * - image2Src (string, required): URL of the bottom/right pill image.
 * - image2Alt (string, required): Alt text for the second image.
 * - stats (array, required): Array of exactly 4 stat objects:
 *     - value (string): The large stat number or label (e.g. "+230%").
 *     - label (string): Short description below the stat.
 *     - bg (string): Tailwind bg class for the desktop card (e.g. "bg-blue-dark1").
 *     - bgMobile (string): Tailwind bg class for the mobile card (e.g. "bg-blue").
 * - buttonText (string, required): CTA button label.
 * - buttonTo (string, required): CTA button route.
 * - buttonColour (string, optional): "blue" or "orange". Defaults to "blue".
 */

const ImpactStatsSection = ({
  bgColor = "bg-blue/20",
  title,
  description,
  image1Src,
  image1Alt,
  image2Src,
  image2Alt,
  stats,
  buttonText,
  buttonTo,
  buttonColour = "blue",
}) => {
  return (
    <section className={`${bgColor} py-16 px-4 md:px-8 lg:px-16 xl:px-24`}>
      <div className="max-w-7xl mx-auto">

        {/* Header */}
        <div className="mb-12 text-center md:text-left max-w-xl mx-auto md:mx-0">
          <h2 className="text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold text-blue-dark1 mb-4">
            {title}
          </h2>
          <p className="!text-base md:!text-lg leading-relaxed">
            {description}
          </p>
        </div>

        {/* Desktop / Tablet grid */}
        <div className="hidden md:grid grid-cols-4 gap-4 lg:gap-6 mb-4 lg:mb-6">
          {/* Pill image — top left */}
          <div className="col-span-2 h-[300px] rounded-l-full rounded-r-full overflow-hidden">
            <img
              src={image1Src}
              alt={image1Alt}
              className="w-full h-full object-cover"
              loading="lazy"
            />
          </div>
          {/* Stats 0 and 1 */}
          {stats.slice(0, 2).map((stat, i) => (
            <div key={i} className={`col-span-1 ${stat.bg} rounded-3xl p-6 lg:p-8 flex flex-col text-white`}>
              <div className="flex-grow flex flex-col items-center justify-center">
                <p className="!text-4xl lg:!text-6xl xl:!text-7xl font-bold">{stat.value}</p>
                <img
                  src="https://sap-app-prod.s3.us-west-2.amazonaws.com/consulting-services/consulting-pages-v2/white-horizontal-line.svg"
                  alt=""
                  className="w-14 lg:w-24 mt-4"
                />
              </div>
              <p className="!text-sm lg:!text-base text-left mt-4">{stat.label}</p>
            </div>
          ))}
        </div>

        <div className="hidden md:grid grid-cols-4 gap-4 lg:gap-6 mb-12">
          {/* Stats 2 and 3 */}
          {stats.slice(2, 4).map((stat, i) => (
            <div key={i} className={`col-span-1 ${stat.bg} rounded-3xl p-6 lg:p-8 flex flex-col text-white`}>
              <div className="flex-grow flex flex-col items-center justify-center">
                <p className="!text-4xl lg:!text-6xl xl:!text-7xl font-bold">{stat.value}</p>
                <img
                  src="https://sap-app-prod.s3.us-west-2.amazonaws.com/consulting-services/consulting-pages-v2/white-horizontal-line.svg"
                  alt=""
                  className="w-14 lg:w-24 mt-4"
                />
              </div>
              <p className="!text-sm lg:!text-base text-left mt-4">{stat.label}</p>
            </div>
          ))}
          {/* Pill image — bottom right */}
          <div className="col-span-2 h-[300px] rounded-l-full rounded-r-full overflow-hidden">
            <img
              src={image2Src}
              alt={image2Alt}
              className="w-full h-full object-cover"
              loading="lazy"
            />
          </div>
        </div>

        {/* Mobile layout */}
        <div className="flex flex-col gap-4 md:hidden mb-12">
          <img src={image1Src} alt={image1Alt} className="w-full rounded-2xl" loading="lazy" />
          <div className="grid grid-cols-2 gap-4">
            {stats.map((stat, i) => (
              <div key={i} className={`${stat.bgMobile} rounded-2xl p-5 flex flex-col text-white`}>
                <p className="!text-3xl font-bold mb-2">{stat.value}</p>
                <img
                  src="https://sap-app-prod.s3.us-west-2.amazonaws.com/consulting-services/consulting-pages-v2/white-horizontal-line.svg"
                  alt=""
                  className="w-10 my-2"
                />
                <p className="!text-xs">{stat.label}</p>
              </div>
            ))}
          </div>
          <img src={image2Src} alt={image2Alt} className="w-full rounded-2xl" loading="lazy" />
        </div>

        <LinkButton
          to={buttonTo}
          colour={buttonColour}
          className="text-sm md:text-lg md:px-4 md:py-2 mx-auto md:mx-0"
        >
          {buttonText}
        </LinkButton>
      </div>
    </section>
  );
};

export default ImpactStatsSection;
